import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { DivIcon, LatLng } from "leaflet";
import "leaflet/dist/leaflet.css";
import init, { leader_index } from "./alto_types/alto_types.js";
import {
  getClusterConfig,
  getClusters,
  getHttpBackendUrl,
  getInitialCluster,
  getWebSocketBackendUrl,
  Cluster,
  MODE,
} from "./config";
import { SeedJs, CertifiedBlockJs, ViewData } from "./types";
import { hexToUint8Array, hexUint8Array } from "./utils";
import { resolveLeaderLocation } from "./leaderLocation";
import {
  ConsensusWorkerPool,
  consensusWorkerCount,
} from "./consensusWorkerPool";
import { createConsensusWorker } from "./createConsensusWorker";
import { scaleTimelineWidth } from "./timeline";
import { getLeaderIndicator, getTimelineIdentifier } from "./timelineIdentifier";
import "./App.css";
import AboutModal from './AboutModal';
import './AboutModal.css';
import StatsSection from "./StatsSection";
import './StatsSection.css';
import KeyInfoModal from './KeyModal';
import MapOverlay from './MapOverlay';
import './MapOverlay.css';
import { useClockSkew } from './useClockSkew';
import ErrorNotification from './ErrorNotification';
import './ErrorNotification.css';
import MaintenancePage from './MaintenancePage';
import SearchModal from './SearchModal';
import './SearchModal.css';

const TIMEOUT_DURATION = 5000; // 5s
const HEALTH_CHECK_INTERVAL = 60000; // Check health every minute
const VIEW_RENDER_INTERVAL = 100;
const MAX_TRACKED_VIEWS = 128;
const MAX_RENDERED_VIEWS = 50;

const retainNewestViews = (views: ViewData[]): ViewData[] => {
  const sorted = [...views].sort((a, b) => b.view - a.view);
  for (const discarded of sorted.slice(MAX_TRACKED_VIEWS)) {
    if (discarded.timeoutId) {
      clearTimeout(discarded.timeoutId);
    }
  }
  return sorted.slice(0, MAX_TRACKED_VIEWS);
};

const center = new LatLng(0, 0);
// ASCII Logo animation logic
const initializeLogoAnimations = () => {
  const horizontalSymbols = [" ", "*", "+", "-", "~"];
  const verticalSymbols = [" ", "*", "+", "|"];
  const edgeSymbols = [" ", "*", "+"];

  function getRandomItem(arr: string[]) {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  function getRandomDuration(min: number) {
    return Math.random() * (10000 - min) + min;
  }

  function updateSymbol(symbol: Element, choices: string[]) {
    symbol.textContent = getRandomItem(choices);
    setTimeout(() => updateSymbol(symbol, choices), getRandomDuration(500));
  }

  document.querySelectorAll('.horizontal-logo-symbol').forEach(symbol => {
    setTimeout(() => updateSymbol(symbol, horizontalSymbols), getRandomDuration(1500));
  });

  document.querySelectorAll('.vertical-logo-symbol').forEach(symbol => {
    setTimeout(() => updateSymbol(symbol, verticalSymbols), getRandomDuration(1500));
  });

  document.querySelectorAll('.edge-logo-symbol').forEach(symbol => {
    setTimeout(() => updateSymbol(symbol, edgeSymbols), getRandomDuration(1500));
  });
};

const App: React.FC = () => {
  const [selectedCluster, setSelectedCluster] = useState<Cluster>(getInitialCluster());
  const clusterConfig = useMemo(() => getClusterConfig(selectedCluster), [selectedCluster]);
  const allConfigs = useMemo(() => getClusters(), []);
  const { BACKEND_URL, PUBLIC_KEY_HEX, LOCATIONS, PARTICIPANTS } = clusterConfig;
  const standardCertificates = clusterConfig.CERTIFICATE_MODE === 'standard';
  const leaderIndicator = getLeaderIndicator(standardCertificates);
  const markerIcon = useMemo(() => new DivIcon({
    className: "custom-div-icon",
    html: `<div style="
          background-color: ${leaderIndicator.color};
          width: 16px;
          height: 16px;
          border-radius: 50%;
          "></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  }), [leaderIndicator.color]);
  const PUBLIC_KEY = useMemo(() => hexToUint8Array(PUBLIC_KEY_HEX), [PUBLIC_KEY_HEX]);

  const [views, setViews] = useState<ViewData[]>([]);
  const mappedView = useMemo(
    () => views.find(view => view.location !== undefined),
    [views],
  );
  const lastObservedViewRef = useRef<number | null>(null);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState<boolean>(false);
  const [isKeyInfoModalOpen, setIsKeyInfoModalOpen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showError, setShowError] = useState<boolean>(false);
  const [isInMaintenance, setIsInMaintenance] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false);
  const healthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const adjustTime = useClockSkew();
  const [currentTime, setCurrentTime] = useState(() => adjustTime(Date.now()));
  const wsRef = useRef<WebSocket | null>(null);
  const currentPoolRef = useRef<ConsensusWorkerPool | null>(null);

  // Manage WebSocket lifecycle
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const performClusterSwitch = useCallback((cluster: Cluster) => {
    console.log(`Switching to ${cluster} cluster`);

    // When switching, we close the old socket. The `onclose` handler for that socket
    // should not trigger a reconnect or error message.
    if (wsRef.current) {
      // Temporarily disable the onclose handler to prevent side-effects.
      wsRef.current.onclose = null;
      wsRef.current.close();
    }

    // Update the selected cluster
    setSelectedCluster(cluster);
  }, []);


  const handleClusterChange = (cluster: Cluster) => {
    if (cluster !== selectedCluster) {
      // Update URL and push to history
      const url = new URL(window.location.href);
      url.searchParams.set('cluster', cluster);
      window.history.pushState({ cluster }, '', url.toString());

      // Perform the cluster switch
      performClusterSwitch(cluster);
    }
  };

  // Effect to handle browser navigation (back/forward)
  useEffect(() => {
    const handlePopState = () => {
      const newCluster = getInitialCluster();
      if (newCluster !== selectedCluster) {
        performClusterSwitch(newCluster);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedCluster, performClusterSwitch]);

  // Reset state when the cluster changes
  useEffect(() => {
    setViews([]);
    lastObservedViewRef.current = null;
    setErrorMessage("");
    setShowError(false);
  }, [selectedCluster]);

  // Health check function
  const checkHealth = useCallback(async () => {
    try {
      const response = await fetch(`${getHttpBackendUrl(BACKEND_URL)}/health`, {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          "Pragma": "no-cache",
          "Expires": "0"
        }
      });

      if (response.status !== 200) {
        console.error(`Health check failed with status: ${response.status}`);
        setIsInMaintenance(true);
        return false;
      } else {
        setIsInMaintenance(false);
        return true;
      }
    } catch (error) {
      console.error("Health check failed:", error);
      setIsInMaintenance(true);
      return false;
    } finally {
      // Mark loading as complete regardless of result
      setIsLoading(false);
    }
  }, [BACKEND_URL]);

  // Run health check on initial load and periodically
  useEffect(() => {
    // Run initial health check immediately - this will set isLoading to false when complete
    checkHealth();

    // Only set up periodic checks after the initial check
    const setupInterval = () => {
      healthCheckIntervalRef.current = setInterval(checkHealth, HEALTH_CHECK_INTERVAL);
    };

    // Wait for the initial check before setting up the interval
    const initialCheckTimeout = setTimeout(setupInterval, 1000);

    return () => {
      // Clean up interval and timeout on unmount
      clearTimeout(initialCheckTimeout);
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
    };
  }, [checkHealth]);

  // Initialize logo animations
  useEffect(() => {
    initializeLogoAnimations();
  }, []);

  // Check for mobile viewport
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkIfMobile();

    // Add resize listener
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  const resolveSeedLocation = useCallback((seed: SeedJs) => {
    if (MODE !== 'public' || LOCATIONS.length === 0) {
      return { location: undefined, locationName: undefined };
    }

    const locationIndex = leader_index(seed, LOCATIONS.length);
    const location = LOCATIONS[locationIndex];
    return {
      location: location?.[0],
      locationName: location?.[1],
    };
  }, [LOCATIONS]);

  // Advance the observed maximum outside React's state updater, which StrictMode may invoke twice.
  // Return the previous maximum for gap filling.
  const observeView = useCallback((view: number): number | null => {
    const lastObservedView = lastObservedViewRef.current;
    if (lastObservedView === null || view > lastObservedView) {
      lastObservedViewRef.current = view;
    }
    return lastObservedView;
  }, []);

  // Insert bounded placeholders for locally unobserved views between verified observations.
  // Both modes use certificates. VRF mode also observes seeds for the next view.
  const fillMissedViews = useCallback((
    newViews: ViewData[],
    lastObservedView: number | null,
    view: number,
    observedAt: number,
  ) => {
    if (lastObservedView === null || view <= lastObservedView + 1) {
      return;
    }

    // Bound gap filling by the number of tracked views.
    const startViewIndex = Math.max(lastObservedView + 1, view - MAX_TRACKED_VIEWS);
    for (let missedView = startViewIndex; missedView < view; missedView++) {
      if (newViews.findIndex((v) => v.view === missedView) !== -1) {
        continue;
      }
      const timeoutId = setTimeout(() => {
        setViews((currentViews) =>
          currentViews.map((v) =>
            v.view === missedView && v.status === "unknown"
              ? { ...v, status: "timed_out", timeoutId: undefined }
              : v,
          ),
        );
      }, TIMEOUT_DURATION);
      newViews.unshift({
        view: missedView,
        location: undefined,
        locationName: undefined,
        status: "unknown",
        startTime: observedAt,
        timeoutId,
      });
    }
  }, []);

  const handleSeed = useCallback((seed: SeedJs, receivedAt: number) => {
    const view = seed.view + 1; // Next view is determined by seed - 1
    const observedAt = adjustTime(receivedAt);
    const lastObservedView = observeView(view);

    setViews((prevViews) => {
      // Create a copy of the current views that we'll modify
      let newViews = [...prevViews];

      fillMissedViews(newViews, lastObservedView, view, observedAt);

      // Check if this view already exists
      const existingIndex = newViews.findIndex(v => v.view === view);

      if (existingIndex !== -1) {
        // Preserve certified timing and status, preferring its known proposer to the seed prediction.
        const existing = newViews[existingIndex];
        if (existing.status === "finalized" || existing.status === "notarized") {
          const { location, locationName } = resolveSeedLocation(seed);
          newViews[existingIndex] = {
            ...existing,
            location: existing.location ?? location,
            locationName: existing.locationName ?? locationName,
            signature: seed.signature,
          };

          return retainNewestViews(newViews);
        }

        // If it exists but is in another state, clear its timeout but preserve everything else
        if (newViews[existingIndex].timeoutId) {
          clearTimeout(newViews[existingIndex].timeoutId);
        }
      }

      // Create the new view data
      const { location, locationName } = resolveSeedLocation(seed);
      const newView: ViewData = {
        view,
        location,
        locationName,
        status: "growing",
        startTime: observedAt,
        signature: seed.signature,
      };

      // Set a timeout for this specific view
      const timeoutId = setTimeout(() => {
        setViews((currentViews) => {
          return currentViews.map((v) => {
            // Only time out this specific view if it's still in growing state
            if (v.view === view && v.status === "growing") {
              return { ...v, status: "timed_out", timeoutId: undefined };
            }
            return v;
          });
        });
      }, TIMEOUT_DURATION);

      // Add timeoutId to the new view
      const viewWithTimeout = { ...newView, timeoutId };

      // Update or add the view
      if (existingIndex !== -1) {
        // Only update if necessary - preserve existing data that shouldn't change
        newViews[existingIndex] = {
          ...newViews[existingIndex],
          status: "growing",
          signature: seed.signature,
          timeoutId: timeoutId,
          location,
          locationName,
        };
      } else {
        // Add as new
        newViews.unshift(viewWithTimeout);
      }

      return retainNewestViews(newViews);
    });
  }, [adjustTime, resolveSeedLocation, observeView, fillMissedViews]);

  const handleNotarization = useCallback((notarized: CertifiedBlockJs, receivedAt: number) => {
    const view = notarized.view;
    const leaderLocation = resolveLeaderLocation(notarized.block.leader, PARTICIPANTS, LOCATIONS);
    const currentTime = adjustTime(receivedAt);
    const blockTime = notarized.block.timestamp;
    const calculatedStartTime = blockTime || currentTime;
    const actualNotarizationLatency = blockTime > 0 && blockTime < currentTime
      ? currentTime - blockTime
      : undefined;
    const lastObservedView = observeView(view);
    setViews((prevViews) => {
      // If the view exists and is already finalized, ignore this notarization completely
      const existing = prevViews.find((v) => v.view === view);
      if (existing && existing.status === "finalized") {
        return prevViews; // No changes needed, preserve finalized state
      }

      // Placeholders are inserted at the front, so locate the view after filling.
      const newViews = [...prevViews];
      fillMissedViews(newViews, lastObservedView, view, currentTime);
      const index = newViews.findIndex((v) => v.view === view);

      if (index !== -1) {
        const viewData = newViews[index];
        // Clear timeout if it exists
        if (viewData.timeoutId) {
          clearTimeout(viewData.timeoutId);
        }

        // Update the view with notarization data
        newViews[index] = {
          ...viewData,
          status: "notarized", // We already checked it's not finalized
          notarizationTime: currentTime,
          // If no start time exists, use the block timestamp
          startTime: viewData.startTime || calculatedStartTime,
          block: viewData.block || notarized.block, // Don't overwrite existing block data
          timeoutId: undefined,
          actualNotarizationLatency,
          ...leaderLocation,
        };
      } else {
        // If view doesn't exist, create it with block timestamp as start time
        newViews.unshift({
          view,
          location: leaderLocation?.location,
          locationName: leaderLocation?.locationName,
          status: "notarized",
          startTime: calculatedStartTime,
          notarizationTime: currentTime,
          block: notarized.block,
          actualNotarizationLatency,
        });
      }

      return retainNewestViews(newViews);
    });
  }, [adjustTime, LOCATIONS, PARTICIPANTS, observeView, fillMissedViews]);

  const handleFinalization = useCallback((finalized: CertifiedBlockJs, receivedAt: number) => {
    const view = finalized.view;
    const leaderLocation = resolveLeaderLocation(finalized.block.leader, PARTICIPANTS, LOCATIONS);
    const currentTime = adjustTime(receivedAt);
    const blockTime = finalized.block.timestamp;
    const calculatedStartTime = blockTime || currentTime;
    const actualFinalizationLatency = blockTime > 0 && blockTime < currentTime
      ? currentTime - blockTime
      : undefined;
    const lastObservedView = observeView(view);
    setViews((prevViews) => {
      // Preserve the first finalization's timing.
      const existing = prevViews.find((v) => v.view === view);
      if (existing && existing.status === "finalized") {
        return prevViews;
      }

      // Placeholders are inserted at the front, so locate the view after filling.
      const newViews = [...prevViews];
      fillMissedViews(newViews, lastObservedView, view, currentTime);
      const index = newViews.findIndex((v) => v.view === view);

      if (index !== -1) {
        const viewData = newViews[index];
        // Clear timeout if it exists
        if (viewData.timeoutId) {
          clearTimeout(viewData.timeoutId);
        }

        // Use existing data if available, without fabricating missing data
        newViews[index] = {
          ...viewData,
          status: "finalized",
          finalizationTime: currentTime,
          // Keep existing notarization time if available, but don't create one if missing
          // Keep existing start time or use block timestamp if none
          startTime: viewData.startTime || calculatedStartTime,
          block: finalized.block,
          timeoutId: undefined,
          actualFinalizationLatency,
          ...leaderLocation,
        };
      } else {
        // If view doesn't exist, create it with just the data we have
        newViews.unshift({
          view,
          location: leaderLocation?.location,
          locationName: leaderLocation?.locationName,
          status: "finalized",
          startTime: calculatedStartTime,
          // No notarization time observed yet
          finalizationTime: currentTime,
          block: finalized.block,
          actualFinalizationLatency,
        });
      }

      return retainNewestViews(newViews);
    });
  }, [adjustTime, LOCATIONS, PARTICIPANTS, observeView, fillMissedViews]);

  // Batch verified updates and advance the display clock even when views are unchanged.
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(adjustTime(Date.now()));
      const verified = currentPoolRef.current?.drain() ?? [];
      for (const { kind, artifact, receivedAt, skipped } of verified) {
        if (skipped) {
          // A processing gap starts a new observation baseline. Later verified observations
          // may still reveal missing views.
          lastObservedViewRef.current = null;
        }
        if (!artifact) {
          continue;
        }
        switch (kind) {
          case 0:
            handleSeed(artifact as SeedJs, receivedAt);
            break;
          case 1:
            handleNotarization(artifact as CertifiedBlockJs, receivedAt);
            break;
          case 2:
            handleFinalization(artifact as CertifiedBlockJs, receivedAt);
            break;
        }
      }
    }, VIEW_RENDER_INTERVAL);
    return () => clearInterval(interval);
  }, [adjustTime, handleSeed, handleNotarization, handleFinalization]);

  // WebSocket connection management with fixed single-connection approach
  useEffect(() => {
    // If loading, don't start
    if (isLoading) return;

    // Skip if in maintenance mode
    if (isInMaintenance) {
      // If there's an existing WebSocket connection, close it
      if (wsRef.current) {
        try {
          const ws = wsRef.current;
          wsRef.current = null;
          ws.close();
        } catch (err) {
          console.error("Error closing WebSocket during maintenance:", err);
        }
      }
      return;
    }

    let cancelled = false;
    let verifierPool: ConsensusWorkerPool | undefined;

    // A stopped verifier cannot serve a new connection. Cancel reconnect work before closing
    // the socket so queued socket events cannot restart this effect's transport.
    const stopConnection = () => {
      cancelled = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (wsRef.current) {
        const ws = wsRef.current;
        wsRef.current = null;
        try {
          ws.close(1000, "Connection stopped");
        } catch (err) {
          console.error("Error closing WebSocket:", err);
        }
      }
    };

    const connectWebSocket = () => {
      // Clear any existing reconnection timers
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Close existing connection (if any)
      if (wsRef.current) {
        try {
          const ws = wsRef.current;
          wsRef.current = null;
          ws.close();
        } catch (err) {
          console.error("Error closing existing WebSocket:", err);
        }
      }

      // Create new WebSocket connection
      const wsCreationTime = Date.now();
      const ws = new WebSocket(`${getWebSocketBackendUrl(BACKEND_URL)}/consensus/ws`);
      wsRef.current = ws;
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        if (cancelled) {
          ws.close(1000, "Connection stopped");
          return;
        }
        console.log(`WebSocket connected: ${BACKEND_URL}`);
        setErrorMessage("");
        setShowError(false);
      };

      ws.onmessage = (event) => {
        const data = new Uint8Array(event.data);
        const kind = data[0];
        if (kind <= 2) {
          verifierPool?.verify(kind, data.slice(1), Date.now());
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      ws.onclose = (event) => {
        if (cancelled) {
          return;
        }
        console.error(`WebSocket closed with code: ${event.code}`);

        // Check for potential rate limiting (code 1006 is "Abnormal Closure")
        if (event.code === 1006) {
          // If connection closed very quickly, likely rate-limited
          const timeSinceStarted = Date.now() - wsCreationTime;
          if (timeSinceStarted < 1000) {
            setErrorMessage("Too many connection attempts from your IP. Try connecting again in a few minutes.");
            setShowError(true);

            // Clear reference to prevent reconnection
            wsRef.current = null;
          } else {
            setErrorMessage("Disconnected from server. Reconnecting...");
            setShowError(true);
          }
        }

        // Only attempt to reconnect if we still have a reference to this websocket (and we didn't detect a rate limit error)
        if (wsRef.current === ws) {
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connectWebSocket();
          }, 11000);
        }
      };
    };

    const setup = async () => {
      await init();
      if (cancelled) {
        return;
      }
      verifierPool = new ConsensusWorkerPool(
        consensusWorkerCount(navigator.hardwareConcurrency || 4),
        createConsensusWorker,
        PUBLIC_KEY,
        standardCertificates,
        () => {
          stopConnection();
          setErrorMessage("A consensus verifier stopped unexpectedly. Refresh to reconnect.");
          setShowError(true);
        },
      );
      currentPoolRef.current = verifierPool;
      connectWebSocket();
    };

    setup().catch((error) => {
      if (cancelled) {
        return;
      }
      console.error("Unable to initialize consensus verifiers:", error);
      setErrorMessage("Consensus verification could not start. Refresh to retry.");
      setShowError(true);
    });

    // Dispose the connection and verifiers when this effect ends.
    return () => {
      stopConnection();
      verifierPool?.terminate();
      currentPoolRef.current = null;
    };
  }, [selectedCluster, isLoading, isInMaintenance, BACKEND_URL, PUBLIC_KEY, standardCertificates]);

  // Loading state - show nothing until we get the result of the health check
  if (isLoading) {
    return null;
  }

  // If we're in maintenance mode, show the maintenance page
  if (isInMaintenance) {
    return <MaintenancePage />;
  }

  return (
    <div className="app-container">
      <ErrorNotification
        message={errorMessage}
        isVisible={showError}
        onDismiss={() => setShowError(false)}
        autoHideDuration={15000}
      />
      <header className="app-header">
        <div className="logo-container">
          <div className="logo-line">
            <span className="edge-logo-symbol">+</span>
            <span className="horizontal-logo-symbol">~</span>
            <span className="horizontal-logo-symbol"> </span>
            <span className="horizontal-logo-symbol">-</span>
            <span className="horizontal-logo-symbol">+</span>
            <span className="horizontal-logo-symbol">-</span>
            <span className="horizontal-logo-symbol">+</span>
            <span className="horizontal-logo-symbol"> </span>
            <span className="horizontal-logo-symbol">-</span>
            <span className="horizontal-logo-symbol">+</span>
            <span className="horizontal-logo-symbol">-</span>
            <span className="horizontal-logo-symbol">~</span>
            <span className="horizontal-logo-symbol">~</span>
            <span className="edge-logo-symbol">*</span>
          </div>
          <div className="logo-line">
            <span className="vertical-logo-symbol">|</span>
            <span className="logo-text"> commonware </span>
            <span className="vertical-logo-symbol"> </span>
          </div>
          <div className="logo-line">
            <span className="edge-logo-symbol">*</span>
            <span className="horizontal-logo-symbol">~</span>
            <span className="horizontal-logo-symbol">+</span>
            <span className="horizontal-logo-symbol">+</span>
            <span className="horizontal-logo-symbol">-</span>
            <span className="horizontal-logo-symbol"> </span>
            <span className="horizontal-logo-symbol">~</span>
            <span className="horizontal-logo-symbol">-</span>
            <span className="horizontal-logo-symbol">+</span>
            <span className="horizontal-logo-symbol"> </span>
            <span className="horizontal-logo-symbol">-</span>
            <span className="horizontal-logo-symbol">*</span>
            <span className="horizontal-logo-symbol">-</span>
            <span className="edge-logo-symbol">+</span>
          </div>
        </div>
        <div className="about-button-container">
          <button
            className="search-header-button"
            onClick={() => setIsSearchModalOpen(true)}
          >
            ⚲
          </button>
          <button
            className="key-header-button"
            onClick={() => setIsKeyInfoModalOpen(true)}
          >
            ⚷︎
          </button>
          <button
            className={`about-header-button ${selectedCluster === 'usa' ? 'usa-cluster' : ''}`}
            onClick={() => setIsAboutModalOpen(true)}
          >
            🌐︎
          </button>
        </div>
      </header>

      <main className="app-main">
        {/* Map - only show in public mode */}
        {MODE === 'public' && (
          <div className="map-container">
            <MapContainer key={selectedCluster} center={center} zoom={1} style={{ height: "100%", width: "100%" }} zoomControl={false} scrollWheelZoom={false} doubleClickZoom={false} touchZoom={false} dragging={false}>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png?key=cb1_29si_1_944f50395a982fb974c926e1"
                attribution='&copy; OSM | &copy; CARTO</a>'
              />
              {mappedView?.location !== undefined && (
                <Marker
                  key={mappedView.view}
                  position={mappedView.location}
                  icon={markerIcon}
                >
                  <Popup>
                    <div>
                      <strong>View: {mappedView.view}</strong><br />
                      Location: {mappedView.locationName}<br />
                      Status: {mappedView.status}<br />
                      {mappedView.block && (
                        <>Block Height: {mappedView.block.height}<br /></>
                      )}
                      {mappedView.startTime && (
                        <>Start Time: {new Date(mappedView.startTime).toLocaleTimeString()}<br /></>
                      )}
                    </div>
                  </Popup>
                </Marker>
              )}
              <MapOverlay numValidators={LOCATIONS.length} />
            </MapContainer>
          </div>
        )}

        {/* Stats Section */}
        <StatsSection
          views={views}
          selectedCluster={selectedCluster}
          onClusterChange={handleClusterChange}
          configs={allConfigs}
        />

        {/* Bars with integrated legend */}
        <div className="bars-container">
          <div className="bars-header">
            <h2 className="bars-title">Timeline</h2>
            <div className="legend-container">
              <LegendItem color={leaderIndicator.color} label={leaderIndicator.label} />
              <LegendItem color={"#000"} label="Locked" />
              <LegendItem color={"#228B22ff"} label="Finalized" />
            </div>
          </div>

          <div className="bars-list">
            {views.slice(0, MAX_RENDERED_VIEWS).map((viewData) => (
              <Bar
                key={viewData.view}
                viewData={viewData}
                currentTime={currentTime}
                isMobile={isMobile}
                standardCertificates={standardCertificates}
              />
            ))}
          </div>
        </div>
      </main >

      <footer className="footer">
        <div className="socials">
          <a href="https://commonware.xyz/hiring.html">Hiring</a>
          <a href="https://github.com/commonwarexyz/alto">GitHub</a>
          <a href="https://x.com/commonwarexyz">X</a>
        </div>
        &copy; {new Date().getFullYear()} Commonware, Inc. All rights reserved.
      </footer>

      <AboutModal
        isOpen={isAboutModalOpen}
        onClose={() => setIsAboutModalOpen(false)}
        clusterConfig={clusterConfig}
      />
      <KeyInfoModal
        isOpen={isKeyInfoModalOpen}
        onClose={() => setIsKeyInfoModalOpen(false)}
        publicKeyHex={clusterConfig.PUBLIC_KEY_HEX}
      />
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        clusterConfig={clusterConfig}
      />
    </div >
  );
};

interface LegendItemProps {
  color: string;
  label: string;
}

const LegendItem: React.FC<LegendItemProps> = ({ color, label }) => {
  return (
    <div className="legend-item">
      <div className="legend-color" style={{ backgroundColor: color }}></div>
      <span className="legend-label">{label}</span>
    </div>
  );
};

interface BarProps {
  viewData: ViewData;
  currentTime: number;
  isMobile: boolean;
  standardCertificates: boolean;
}

// Replace the existing Bar component with this updated version

const Bar: React.FC<BarProps> = ({ viewData, currentTime, isMobile, standardCertificates }) => {
  const { view, status, startTime, notarizationTime, finalizationTime, signature, block, actualNotarizationLatency, actualFinalizationLatency } = viewData;
  const timelineIdentifier = getTimelineIdentifier(standardCertificates, signature, block?.digest);
  const [measuredWidth, setMeasuredWidth] = useState(isMobile ? 200 : 500); // Reasonable default
  const barContainerRef = useRef<HTMLDivElement>(null);

  // Measure width after component mounts and on resize
  useEffect(() => {
    const updateWidth = () => {
      if (barContainerRef.current) {
        const width = barContainerRef.current.clientWidth - (isMobile ? 4 : 8);
        setMeasuredWidth(width);
      }
    };

    // Initial measurement
    updateWidth();

    // Add resize listener
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [isMobile]);

  const viewInfoWidth = isMobile ? 50 : 80;
  const minBarWidth = isMobile ? 30 : 60; // Minimum width for completed bars
  const minSegmentWidth = isMobile ? 15 : 30; // Minimum segment width

  // Calculate widths for different stages
  let totalWidth = 0;
  let notarizedWidth = 0;
  let finalizedWidth = 0;

  // Get actual latency values for calculations
  let growingLatency = 0;
  let notarizedLatency = 0;
  let finalizedLatency = 0;

  // Format timing texts with improved clarity
  let inBarText = ""; // Text to display inside the bar (block info only)
  let notarizedLatencyText = ""; // Text to display below the notarized point
  let finalizedLatencyText = ""; // Text to display below the finalized point
  let growingLatencyText = ""; // Text to display below the growing bar tip

  // Calculate latencies and set text
  if (status === "growing" || status === "unknown") {
    growingLatency = currentTime - startTime;
    if (growingLatency > 1) {
      growingLatencyText = `${Math.round(growingLatency)}ms`;
    }
  } else if (status === "notarized") {
    if (actualNotarizationLatency) {
      notarizedLatency = actualNotarizationLatency;
      notarizedLatencyText = `${Math.round(notarizedLatency)}ms`;
    } else if (notarizationTime) {
      notarizedLatency = notarizationTime - startTime;
      if (notarizedLatency > 0) {
        notarizedLatencyText = `${Math.round(notarizedLatency)}ms`;
      }
    }
  } else if (status === "finalized") {
    // Calculate notarization latency if available
    if (notarizationTime) {
      if (actualNotarizationLatency) {
        notarizedLatency = actualNotarizationLatency;
        notarizedLatencyText = `${Math.round(notarizedLatency)}ms`;
      } else {
        notarizedLatency = notarizationTime - startTime;
        if (notarizedLatency > 0) {
          notarizedLatencyText = `${Math.round(notarizedLatency)}ms`;
        }
      }
    }

    // Calculate finalization latency
    if (actualFinalizationLatency) {
      finalizedLatency = actualFinalizationLatency;
      finalizedLatencyText = `${Math.round(finalizedLatency)}ms`;
    } else if (finalizationTime) {
      finalizedLatency = finalizationTime - startTime;
      if (finalizedLatency > 0) {
        finalizedLatencyText = `${Math.round(finalizedLatency)}ms`;
      }
    }
  }

  // Calculate the widths for different bar segments
  if (status === "growing" || status === "unknown") {
    totalWidth = scaleTimelineWidth(growingLatency, measuredWidth);
    // Ensure growing bars are visible but don't exceed available width
    totalWidth = Math.min(Math.max(totalWidth, growingLatency > 50 ? minSegmentWidth : 0), measuredWidth);
  } else if (status === "notarized") {
    totalWidth = scaleTimelineWidth(notarizedLatency, measuredWidth);
    // Ensure notarized bars meet minimum width
    totalWidth = Math.max(totalWidth, minBarWidth);
  } else if (status === "finalized") {
    if (notarizationTime) {
      // Calculate notarized segment width
      notarizedWidth = scaleTimelineWidth(notarizedLatency, measuredWidth);
      notarizedWidth = Math.max(notarizedWidth, minSegmentWidth);

      // Calculate finalized segment width (difference between finalization and notarization)
      const finalizationDelta = finalizedLatency - notarizedLatency;
      if (finalizationDelta > 0) {
        finalizedWidth = scaleTimelineWidth(finalizationDelta, measuredWidth);
        finalizedWidth = Math.max(finalizedWidth, minSegmentWidth / 2);
      }

      totalWidth = notarizedWidth + finalizedWidth;
    } else {
      // Without notarization time, use the entire bar for finalization
      totalWidth = scaleTimelineWidth(finalizedLatency, measuredWidth);
      totalWidth = Math.max(totalWidth, minBarWidth);
    }
  } else if (status === "timed_out") {
    // Timed out - always full width
    totalWidth = measuredWidth;
  }

  // Ensure total width doesn't exceed available space
  totalWidth = Math.min(totalWidth, measuredWidth);

  // Set block info text
  if (status === "timed_out") {
    inBarText = "MISSING";
  } else if (status === "unknown") {
    inBarText = "PENDING";
  } else if (block) {
    inBarText = `#${block.height} | ${hexUint8Array(block.digest)}`;
  }

  // Calculate positions for timing labels to prevent overlap
  const labelWidth = isMobile ? 30 : 45; // Estimated width of a timing label
  const minLabelSpacing = labelWidth + 5; // Increased minimum space needed between labels

  // Calculate ideal positions for notarization and finalization labels (centered on their respective points)
  let growingLabelPosition = Math.max(0, totalWidth - (labelWidth / 2));
  let notarizedLabelPosition = notarizedWidth > 0 ? Math.max(0, notarizedWidth - (labelWidth / 2)) : 0;
  let finalizedLabelPosition = totalWidth > 0 ? Math.max(0, totalWidth - (labelWidth / 2)) : 0;

  // Constraint to ensure labels don't overflow right edge
  const maxLabelPosition = measuredWidth - labelWidth;
  growingLabelPosition = Math.min(growingLabelPosition, maxLabelPosition);
  notarizedLabelPosition = Math.min(notarizedLabelPosition, maxLabelPosition);
  finalizedLabelPosition = Math.min(finalizedLabelPosition, maxLabelPosition);

  // Check if labels would overlap
  const wouldOverlap = status === "finalized" &&
    notarizationTime &&
    (finalizedLabelPosition - notarizedLabelPosition < minLabelSpacing);

  // Adjust positions if overlap detected
  if (wouldOverlap) {
    // Prioritize the finalization label position since it's usually more important
    // Then push the notarization label to the left to ensure minimum spacing
    notarizedLabelPosition = finalizedLabelPosition - minLabelSpacing;

    // If this would push the notarization label off the left edge, adjust both
    if (notarizedLabelPosition < 0) {
      notarizedLabelPosition = 0;

      // Only move the finalization label if there's enough room for both
      if (minLabelSpacing < totalWidth) {
        finalizedLabelPosition = minLabelSpacing;
      } else {
        // Not enough room for both, keep finalization at the far right
        finalizedLabelPosition = totalWidth - (labelWidth / 2);
      }
    }
  }

  // Determine what content to render in bar - for finalized without notarization
  const renderFinalizedWithoutNotarization = status === "finalized" && !notarizationTime;

  return (
    <div className="bar-row">
      <div className="view-info" style={{ width: `${viewInfoWidth}px` }}>
        <div className="view-number">{view}</div>
        <div
          className="view-identifier"
          style={{ color: timelineIdentifier.color }}
          title={timelineIdentifier.fullValue ? `${timelineIdentifier.label}: ${timelineIdentifier.fullValue}` : undefined}
        >
          {timelineIdentifier.value}
        </div>
      </div>

      <div className="bar-container" ref={barContainerRef}>
        {/* Main bar container */}
        <div
          className="bar-main"
          style={{
            width: `${totalWidth}px`,
          }}
        >
          {/* Timed out or Growing state */}
          {(status === "timed_out" || status === "growing" || status === "unknown") && (
            <div
              className={`bar-segment ${status === "timed_out" ? "timed-out" :
                status === "unknown" ? "unknown" : "growing"
                }`}
              style={{ width: "100%" }}
            >
              {inBarText}
            </div>
          )}

          {/* Notarized state */}
          {status === "notarized" && (
            <>
              <div
                className="bar-segment growing"
                style={{ width: "100%" }}
              >
                {inBarText}
              </div>
              <div
                className="marker notarization-marker"
                style={{
                  right: 0,
                }}
              />
            </>
          )}

          {/* Finalized state with notarization */}
          {status === "finalized" && !renderFinalizedWithoutNotarization && (
            <>
              {/* Base segment (proposal or seed to notarization) */}
              <div
                className="bar-segment growing"
                style={{ width: `${notarizedWidth}px` }}
              >
                {inBarText}
              </div>

              {/* Add notarization marker at the junction point between segments */}
              <div
                className="marker notarization-marker"
                style={{
                  left: `${notarizedWidth}px`,
                  right: 'auto',
                }}
                title="Notarization point"
              />

              {/* Notarized to finalized segment */}
              <div
                className="bar-segment finalized"
                style={{
                  left: `${notarizedWidth}px`,
                  width: `${finalizedWidth}px`,
                }}
              />
            </>
          )}

          {/* Finalized state without notarization - just a single finalized bar */}
          {renderFinalizedWithoutNotarization && (
            <div
              className="bar-segment finalized"
              style={{ width: "100%" }}
            >
              {inBarText}
            </div>
          )}

          {/* Marker for finalization point */}
          {status === "finalized" && (
            <div
              className="marker finalization-marker"
              style={{
                right: 0,
              }}
            />
          )}
        </div>

        {/* Timing information underneath */}
        <div className="timing-info">
          {/* Show timing for all states that need it */}
          {(standardCertificates || signature || status === "unknown") && (
            <>
              {/* Latency at notarization point - only show if text exists and we have notarization */}
              {!renderFinalizedWithoutNotarization &&
                (status === "notarized" || status === "finalized") &&
                notarizedWidth > 0 &&
                notarizedLatencyText && (
                  <div
                    className="latency-text notarized-latency"
                    style={{
                      left: `${notarizedLabelPosition}px`,
                      color: "#000",
                    }}
                  >
                    {notarizedLatencyText}
                  </div>
                )}

              {/* Total latency marker for finalized views - only show if text exists */}
              {status === "finalized" && finalizedLatencyText && (
                <div
                  className="latency-text finalized-latency"
                  style={{
                    left: `${finalizedLabelPosition}px`,
                    color: "#228B22ff",
                  }}
                >
                  {finalizedLatencyText}
                </div>
              )}

              {/* Latency for growing bars - follows the tip - only show if text exists */}
              {(status === "growing" || status === "unknown") && growingLatencyText && (
                <div
                  className="latency-text growing-latency"
                  style={{
                    left: `${growingLabelPosition}px`,
                  }}
                >
                  {growingLatencyText}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default App;
